import { jwtDecode } from "jwt-decode";
import { createContext, FC, PropsWithChildren, useContext, useEffect, useState, useCallback } from "react";

interface UserContextType {
    decodedToken: DecodedToken | null;
    isLoading: boolean;
    error: string | null;
    refreshUser: () => void;
}

export interface DecodedToken {
    user: {
        id: number;
        firstname: string;
        lastname: string;
        email: string;
    };
    settings: {
        id: number;
        settings_type: string;
        selected_vehicle_id: number;
        select_all: boolean;
    };
    permissions: {
        settings_type: string;
        user_role: string;
        currency: string;
    };
    iat: number;
    exp: number;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUserContext = () => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUserContext must be used within a UserProvider');
    }
    return context;
};

export const UserProvider: FC<PropsWithChildren> = ({ children }) => {
    const [decodedToken, setDecodedToken] = useState<DecodedToken | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const decodeToken = useCallback(() => {
        try {
            setIsLoading(true);
            setError(null);

            const token = localStorage.getItem("access_token");
            if (!token) {
                setDecodedToken(null);
                return;
            }

            const decodedToken = jwtDecode(token) as DecodedToken;

            // Check if token is expired
            const currentTime = Date.now() / 1000;
            if (decodedToken.exp && decodedToken.exp < currentTime) {
                localStorage.removeItem("access_token");
                setDecodedToken(null);
                setError("Token has expired");
                return;
            }

            setDecodedToken(decodedToken);
        } catch (err) {
            console.error("Failed to decode token:", err);
            setError("Invalid token");
            setDecodedToken(null);
            localStorage.removeItem("access_token");
        } finally {
            setIsLoading(false);
        }
    }, []);

    const refreshUser = useCallback(() => {
        decodeToken();
    }, [decodeToken]);

    useEffect(() => {
        decodeToken();
    }, [decodeToken]);

    // Listen for storage changes to sync across tabs
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === "access_token") {
                decodeToken();
            }
        };

        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, [decodeToken]);

    const value: UserContextType = {
        decodedToken,
        isLoading,
        error,
        refreshUser,
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};